import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AppBar,
  Box,
  Chip,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popover,
  Popper,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import MenuOpenRoundedIcon from '@mui/icons-material/MenuOpenRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import SettingsApplicationsRoundedIcon from '@mui/icons-material/SettingsApplicationsRounded';
import RuleRoundedIcon from '@mui/icons-material/RuleRounded';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import WbTwilightRoundedIcon from '@mui/icons-material/WbTwilightRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import SystemUpdateAltRoundedIcon from '@mui/icons-material/SystemUpdateAltRounded';
import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded';
import { FaUserCircle } from 'react-icons/fa';
import { FiLogOut } from 'react-icons/fi';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Toaster } from 'sonner';
import { ChangePasswordModal, ChatBot, CommonButton, CommonConfirmDialog, CommonLoader, CommonModal, ErrorBoundary, ProtectedModuleRoute } from './components';
import {
  createSession,
  endSession,
  forceChangePassword as forceChangePasswordRequest,
  forgotPassword as forgotPasswordRequest,
  getVersionData,
  login,
  resetPassword as resetPasswordRequest,
  verifyOtp as verifyOtpRequest,
} from './api';
import { IdleTimer, type IdleLogoutReason } from './utils/idleTimer';
import { APP_METADATA } from './config/appMeta';
import { getAllowedModuleNames, isPrivilegedRoleName, normalizeModuleName } from './utils/permissions';
import { showToast } from './utils/toast';
import type { DepartmentKey } from './data/departmentsData';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const VerifyOtpPage = lazy(() => import('./pages/VerifyOtpPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const ForceChangePasswordPage = lazy(() => import('./pages/ForceChangePasswordPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));
const RoleManagementPage = lazy(() => import('./pages/RoleManagementPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const DepartmentsModulePage = lazy(() => import('./pages/DepartmentsModulePage'));
const AssignmentTrackerPage = lazy(() => import('./pages/AssignmentTrackerPage'));
const ApprovalsFlowPage = lazy(() => import('./pages/ApprovalsFlowPage'));
const AuditApprovalsPage = lazy(() => import('./pages/AuditApprovalsPage'));
const DisputeActionsPage = lazy(() => import('./pages/DisputeActionsPage'));
const EmailTemplatesPage = lazy(() => import('./pages/EmailTemplatesPage'));
const ClientsModulePage = lazy(() => import('./pages/ClientsModulePage'));
const PayerModulePage = lazy(() => import('./pages/PayerModulePage'));
const ArCategoriesModulePage = lazy(() => import('./pages/ArCategoriesModulePage'));
const ErrorParametersModulePage = lazy(() => import('./pages/ErrorParametersModulePage'));
const SupportModulePage = lazy(() => import('./pages/SupportModulePage'));
const TicketBucketPage = lazy(() => import('./pages/TicketBucketPage'));
const ReportsModulePage = lazy(() => import('./pages/ReportsModulePage'));
const AuditLogsPage = lazy(() => import('./pages/AuditLogsPage'));
const TaskManagerPage = lazy(() => import('./pages/TaskManagerPage'));
const ARAuditPage = lazy(() => import('./pages/ARAuditPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

const ProfileIcon = FaUserCircle as unknown as React.ComponentType<{ size?: number }>;
const LogoutIcon = FiLogOut as unknown as React.ComponentType<{ size?: number }>;

// Design tokens — see the "Swagpro theme" reference (colors, type, components):
// teal600/700 are the two brand teals (buttons/active vs. links/headings), teal50
// is the tint used for badges and subtle fills, blushBg is the page background,
// surface is card/sidebar white, ink is body text. Kept as BRAND (not renamed)
// since it's referenced throughout this file already.
const BRAND = {
  heading: '#2F7E8E', // teal 700
  card: '#3A8FA0', // teal 600
  tint: '#EAF3F5', // teal 50
  bg: '#F6F1F2', // blush bg
  surface: '#FFFFFF',
  button: '#3A8FA0', // teal 600
  text: '#1E2A30', // ink
  border: '#d9e7ea',
};

const theme = createTheme({
  typography: {
    fontFamily: ['Inter', '-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'Roboto', 'sans-serif'].join(','),
    h1: { color: BRAND.text },
    h2: { color: BRAND.text },
    h3: { color: BRAND.text },
    h4: { color: BRAND.text, fontWeight: 700 },
    h5: { color: BRAND.text, fontWeight: 700 },
    h6: { color: BRAND.text, fontWeight: 700 },
  },
  palette: {
    primary: {
      main: BRAND.card,
      dark: BRAND.heading,
    },
    secondary: {
      main: BRAND.button,
    },
    background: {
      default: BRAND.bg,
      paper: BRAND.surface,
    },
    text: {
      primary: BRAND.text,
      secondary: '#5b6b72',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(90deg, #3E8C9C 0%, #6AB0C0 100%)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: BRAND.surface,
          color: BRAND.text,
          border: `1px solid ${BRAND.border}`,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 10,
        },
        contained: {
          backgroundColor: BRAND.button,
          color: '#ffffff',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: BRAND.heading,
            boxShadow: 'none',
          },
          '&.Mui-disabled': {
            backgroundColor: '#e3eaec',
            color: '#9aa7ac',
          },
        },
        outlined: {
          borderColor: BRAND.heading,
          color: BRAND.heading,
          '&:hover': {
            borderColor: BRAND.heading,
            backgroundColor: BRAND.tint,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 700,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 10,
          minHeight: 36,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          borderRadius: 10,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: BRAND.border,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: BRAND.heading,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: BRAND.heading,
            borderWidth: 2,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          border: `1px solid ${BRAND.border}`,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#5b6b72',
          '&.Mui-focused': {
            color: BRAND.heading,
          },
          '&.MuiInputLabel-shrink': {
            backgroundColor: '#ffffff',
            paddingLeft: 6,
            paddingRight: 6,
            borderRadius: 8,
          },
        },
      },
    },
    // One place to shrink every table in the app — CommonTable renders plain
    // MUI Table components with no font-size of its own, so this is what
    // actually controls row/header text size everywhere.
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: 13,
          padding: '8px 12px',
        },
        head: {
          fontSize: 11.5,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
          color: '#5b6b72',
        },
      },
    },
  },
});

export type AppView =
  | 'not-found'
  | 'login'
  | 'forgot-password'
  | 'verify-otp'
  | 'reset-password'
  | 'force-change-password'
  | 'dashboard'
  | 'user-management'
  | 'role-management'
  | 'reports-module'
  | 'audit-logs'
  | 'departments-module'
  | 'assignment-tracker'
  | 'approvals-flow'
  | 'audit-approvals'
  | 'dispute-actions'
  | 'email-templates'
  | 'support-module'
  | 'ticket-bucket'
  | 'task-manager'
  | 'ar-audit'
  | 'clients-module'
  | 'ar-categories-module'
  | 'error-parameters-module'
  | 'payer-module'
  | 'settings-workflow';

const SIDEBAR_EXPANDED_WIDTH = 250;
const SIDEBAR_COLLAPSED_WIDTH = 76;

const navItems: Array<{ key: AppView; label: string; icon: React.ReactElement }> = [
  // { key: 'login', label: 'Login', icon: <LoginRoundedIcon /> },
  // { key: 'forgot-password', label: 'Forgot Password', icon: <LockResetRoundedIcon /> },
  // { key: 'verify-otp', label: 'Verify OTP', icon: <MarkEmailReadRoundedIcon /> },
  // { key: 'reset-password', label: 'Reset Password', icon: <PasswordRoundedIcon /> },
  { key: 'dashboard', label: 'Dashboard', icon: <DashboardRoundedIcon /> },
  { key: 'user-management', label: 'User Management', icon: <PeopleAltRoundedIcon /> },
  { key: 'role-management', label: 'Role Management', icon: <SecurityRoundedIcon /> },
  { key: 'departments-module', label: 'Departments Module', icon: <ApartmentRoundedIcon /> },
  { key: 'assignment-tracker', label: 'Assignment Tracker', icon: <AssignmentTurnedInRoundedIcon /> },
  { key: 'approvals-flow', label: 'Approvals Flow', icon: <VerifiedRoundedIcon /> },
  { key: 'audit-approvals', label: 'Audit Approvals', icon: <FactCheckRoundedIcon /> },
  { key: 'dispute-actions', label: 'Dispute Actions', icon: <GavelRoundedIcon /> },
  { key: 'email-templates', label: 'Email Templates', icon: <EmailRoundedIcon /> },
  { key: 'support-module', label: 'Support Module', icon: <SupportAgentRoundedIcon /> },
  { key: 'ticket-bucket', label: 'Ticket Bucket', icon: <ConfirmationNumberRoundedIcon /> },
  { key: 'task-manager', label: 'Task Manager', icon: <TaskAltRoundedIcon /> },
  { key: 'ar-audit', label: 'AR Audit', icon: <PendingActionsRoundedIcon /> },
  { key: 'reports-module', label: 'Reports Module', icon: <AssessmentRoundedIcon /> },
  { key: 'audit-logs', label: 'Audit Logs', icon: <HistoryRoundedIcon /> },
  { key: 'settings-workflow', label: 'Settings', icon: <SettingsRoundedIcon /> },
];

const masterModuleItems: Array<{ key: AppView; label: string; icon: React.ReactElement }> = [
  { key: 'clients-module', label: 'Clients Module', icon: <BusinessRoundedIcon /> },
  { key: 'ar-categories-module', label: 'AR Category & Sub-Category', icon: <CategoryRoundedIcon /> },
  { key: 'error-parameters-module', label: 'Error Parameters', icon: <RuleRoundedIcon /> },
  { key: 'payer-module', label: 'Payer Module', icon: <PaidRoundedIcon /> },
];

const APP_VIEW_LABEL: Record<AppView, string> = {
  'not-found': 'Not Found',
  login: 'Login',
  'forgot-password': 'Forgot Password',
  'verify-otp': 'Verify OTP',
  'reset-password': 'Reset Password',
  'force-change-password': 'Set New Password',
  dashboard: 'Dashboard',
  'user-management': 'User Management',
  'role-management': 'Role Management',
  'departments-module': 'Departments Module',
  'assignment-tracker': 'Assignment Tracker',
  'approvals-flow': 'Approvals Flow',
  'audit-approvals': 'Audit Approvals',
  'dispute-actions': 'Dispute Actions',
  'email-templates': 'Email Templates',
  'support-module': 'Support Module',
  'ticket-bucket': 'Ticket Bucket',
  'task-manager': 'Task Manager',
  'ar-audit': 'AR Audit',
  'clients-module': 'Clients Module',
  'ar-categories-module': 'AR Category & Sub-Category',
  'error-parameters-module': 'Error Parameters',
  'payer-module': 'Payer Module',
  'reports-module': 'Reports Module',
  'audit-logs': 'Audit Logs',
  'settings-workflow': 'Settings',
};

const MODULE_NAME_TO_VIEW: Record<string, AppView> = {
  dashboard: 'dashboard',
  'user management': 'user-management',
  'role management': 'role-management',
  'departments module': 'departments-module',
  'assignment tracker': 'assignment-tracker',
  'approvals flow': 'approvals-flow',
  'audit approvals': 'audit-approvals',
  'dispute actions': 'dispute-actions',
  'email templates': 'email-templates',
  'support module': 'support-module',
  'ticket bucket': 'ticket-bucket',
  'task manager': 'task-manager',
  'ar audit': 'ar-audit',
  'clients module': 'clients-module',
  'ar category module': 'ar-categories-module',
  'ar category': 'ar-categories-module',
  'ar category sub category': 'ar-categories-module',
  'ar category & sub category': 'ar-categories-module',
  'error parameters': 'error-parameters-module',
  'error parameters module': 'error-parameters-module',
  'ar error parameters': 'error-parameters-module',
  'payer module': 'payer-module',
  'reports module': 'reports-module',
  'audit logs': 'audit-logs',
  settings: 'settings-workflow',
};

const LAST_AUTH_VIEW_STORAGE_KEY = 'medical-billing-last-auth-view';
const SESSION_ID_STORAGE_KEY = 'medical-billing-session-id';
const SESSION_LOGIN_AT_KEY = 'medical-billing-session-login-at';
const AUTHENTICATED_VIEWS: AppView[] = [
  'dashboard',
  'user-management',
  'role-management',
  'departments-module',
  'assignment-tracker',
  'approvals-flow',
  'audit-approvals',
  'dispute-actions',
  'email-templates',
  'support-module',
  'ticket-bucket',
  'task-manager',
  'ar-audit',
  'reports-module',
  'audit-logs',
  'clients-module',
  'ar-categories-module',
  'error-parameters-module',
  'payer-module',
  'settings-workflow',
];
const APP_VERSION_STORAGE_KEY = 'medical-billing-app-version';

type StoredUser = {
  email?: string;
  role_name?: string;
  name?: string;
  first_login?: boolean;
};

type JwtPayload = {
  exp?: number;
  email?: string;
  role_name?: string;
  name?: string;
};

const parseStoredUser = (): StoredUser | null => {
  const raw = localStorage.getItem('authUser') || localStorage.getItem('loginUser') || localStorage.getItem('user');
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as StoredUser;
  } catch {
    return null;
  }
};

const getAllowedViewsFromUserPermissions = (): AppView[] => {
  const moduleNames = getAllowedModuleNames();
  const views = Array.from(new Set(moduleNames.map((name) => MODULE_NAME_TO_VIEW[normalizeModuleName(name)]).filter((item): item is AppView => Boolean(item))));

  return views.length > 0 ? views : ['dashboard'];
};

const parseJwtPayload = (token: string): JwtPayload | null => {
  const segments = token.split('.');
  if (segments.length !== 3) {
    return null;
  }

  try {
    const base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const decoded = atob(padded);
    return JSON.parse(decoded) as JwtPayload;
  } catch {
    return null;
  }
};

const isTokenExpired = (payload: JwtPayload | null): boolean => {
  if (!payload?.exp) {
    return false;
  }

  const nowSeconds = Math.floor(Date.now() / 1000);
  return payload.exp <= nowSeconds;
};

type InitialSessionState = {
  initialView: AppView;
  initialEmail: string;
  initialRoleName: string;
  tokenExpired: boolean;
};

const hasUnknownPath = (): boolean => {
  const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/';
  return normalizedPath !== '/';
};

const getInitialSessionState = (): InitialSessionState => {
  if (hasUnknownPath()) {
    return { initialView: 'not-found', initialEmail: '', initialRoleName: '', tokenExpired: false };
  }

  const token = localStorage.getItem('authToken') || localStorage.getItem('token');
  const storedUser = parseStoredUser();
  const tokenPayload = token ? parseJwtPayload(token) : null;

  if (!token) {
    return { initialView: 'login', initialEmail: '', initialRoleName: '', tokenExpired: false };
  }

  if (isTokenExpired(tokenPayload)) {
    return { initialView: 'login', initialEmail: '', initialRoleName: '', tokenExpired: true };
  }

  const restoredEmail = storedUser?.email || tokenPayload?.email || '';
  const restoredRoleName = storedUser?.role_name || tokenPayload?.role_name || '';
  const rawLastView = localStorage.getItem(LAST_AUTH_VIEW_STORAGE_KEY);
  const restoredView = AUTHENTICATED_VIEWS.includes(rawLastView as AppView)
    ? (rawLastView as AppView)
    : 'dashboard';

  // Overrides any stored last-view — a password change forced on this account
  // isn't optional, so a refresh (or reopening the tab) mid-flow must not let
  // them land anywhere else.
  if (storedUser?.first_login === true) {
    return {
      initialView: 'force-change-password',
      initialEmail: restoredEmail,
      initialRoleName: restoredRoleName,
      tokenExpired: false,
    };
  }

  return {
    initialView: restoredView,
    initialEmail: restoredEmail,
    initialRoleName: restoredRoleName,
    tokenExpired: false,
  };
};

function App() {
  const initialSessionRef = useRef<InitialSessionState>(getInitialSessionState());
  const [view, setView] = useState<AppView>(initialSessionRef.current.initialView);
  const [activeEmail, setActiveEmail] = useState(initialSessionRef.current.initialEmail);
  const [activeRoleName, setActiveRoleName] = useState(initialSessionRef.current.initialRoleName);
  const [otpFlow, setOtpFlow] = useState<'login' | 'forgot-password'>('login');
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [profileAnchorEl, setProfileAnchorEl] = useState<HTMLElement | null>(null);
  const [masterMenuAnchorEl, setMasterMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [allowedSidebarViews, setAllowedSidebarViews] = useState<AppView[]>(() => getAllowedViewsFromUserPermissions());
  const [currentVersion, setCurrentVersion] = useState<string>(APP_METADATA.version);
  const [currentLastUpdated, setCurrentLastUpdated] = useState<string>(APP_METADATA.lastUpdated);
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [latestVersion, setLatestVersion] = useState<string>('');
  const [latestUpdateTitle, setLatestUpdateTitle] = useState<string>('');
  const [latestUpdateDescription, setLatestUpdateDescription] = useState<string>('');
  const [isModuleLoading, setIsModuleLoading] = useState(false);
  const transitionTimerRef = useRef<number | null>(null);
  const masterMenuCloseTimerRef = useRef<number | null>(null);
  const didRestoreSessionRef = useRef(false);
  const sessionIdRef = useRef<string | null>(localStorage.getItem(SESSION_ID_STORAGE_KEY));
  const idleTimerRef = useRef<IdleTimer | null>(null);
  // Lets a Dashboard KPI card deep-link straight to a specific Departments Module
  // tab (e.g. Payment Posting) instead of just landing on its default tab.
  const [departmentsInitialTab, setDepartmentsInitialTab] = useState<DepartmentKey | undefined>(undefined);
  const showSidebar =
    view === 'dashboard' ||
    view === 'user-management' ||
    view === 'role-management' ||
    view === 'departments-module' ||
    view === 'assignment-tracker' ||
    view === 'approvals-flow' ||
    view === 'audit-approvals' ||
    view === 'dispute-actions' ||
    view === 'email-templates' ||
    view === 'support-module' ||
    view === 'ticket-bucket' ||
    view === 'task-manager' ||
    view === 'ar-audit' ||
    view === 'reports-module' ||
    view === 'audit-logs' ||
    view === 'clients-module' ||
    view === 'ar-categories-module' ||
    view === 'error-parameters-module' ||
    view === 'payer-module' ||
    view === 'settings-workflow';

  const visibleNavItems = useMemo(() => {
    return navItems.filter((item) => allowedSidebarViews.includes(item.key));
  }, [allowedSidebarViews]);

  const visibleMasterModuleItems = useMemo(() => {
    return masterModuleItems.filter((item) => allowedSidebarViews.includes(item.key));
  }, [allowedSidebarViews]);

  const hasDashboardNavItem = useMemo(() => {
    return visibleNavItems.some((item) => item.key === 'dashboard');
  }, [visibleNavItems]);

  const isMasterModuleActive = useMemo(() => {
    return masterModuleItems.some((item) => item.key === view);
  }, [view]);

  const isMasterMenuOpen = Boolean(masterMenuAnchorEl) && visibleMasterModuleItems.length > 0;

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) {
      return 'Good Morning';
    }
    if (hour < 17) {
      return 'Good Afternoon';
    }
    return 'Good Evening';
  }, []);

  const formattedTime = useMemo(() => {
    return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }, []);

  const profileName = useMemo(() => {
    if (!activeEmail) {
      return 'User';
    }

    return activeEmail
      .split('@')[0]
      .split(/[._-]/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }, [activeEmail]);

  const isAdminUser = useMemo(() => isPrivilegedRoleName(activeRoleName), [activeRoleName]);

  const formattedLastUpdated = useMemo(() => {
    const parsed = new Date(currentLastUpdated);

    if (Number.isNaN(parsed.getTime())) {
      return currentLastUpdated;
    }

    return parsed.toLocaleDateString([], {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  }, [currentLastUpdated]);

  const fetchAndCheckVersion = useCallback(async (): Promise<void> => {
    try {
      const response = await getVersionData();

      if (!response.success || !response.data) {
        return;
      }

      const rawData = response.data as Record<string, unknown>;

      const versionPayload = (
        'version_number' in rawData
          ? rawData
          : ((rawData.version as Record<string, unknown> | undefined) ||
            (rawData.item as Record<string, unknown> | undefined) ||
            (rawData.current as Record<string, unknown> | undefined))
      ) as Record<string, unknown> | undefined;

      const serverVersion = String(versionPayload?.version_number || '').trim();
      const updateTitle = String(versionPayload?.update_title || '').trim();
      const updateDescription = String(versionPayload?.update_description || '').trim();
      const lastUpdatedCandidate =
        String(versionPayload?.last_updated_at || versionPayload?.updated_at || versionPayload?.updatedAt || '').trim();

      if (!serverVersion) {
        return;
      }

      setCurrentVersion(serverVersion);
      if (lastUpdatedCandidate) {
        setCurrentLastUpdated(lastUpdatedCandidate);
      }

      const localVersion = localStorage.getItem(APP_VERSION_STORAGE_KEY);
      if (!localVersion) {
        localStorage.setItem(APP_VERSION_STORAGE_KEY, serverVersion);
        return;
      }

      if (localVersion !== serverVersion) {
        setLatestVersion(serverVersion);
        setLatestUpdateTitle(updateTitle);
        setLatestUpdateDescription(updateDescription);
        setVersionDialogOpen(true);
      }
    } catch {
      // Silent fail: version check should not block user workflow.
    }
  }, []);

  const handleHardRefresh = (): void => {
    if (latestVersion) {
      localStorage.setItem(APP_VERSION_STORAGE_KEY, latestVersion);
    }

    const url = new URL(window.location.href);
    url.searchParams.set('_hard_refresh', String(Date.now()));
    window.location.replace(url.toString());
  };

  const handleLogout = useCallback((logoutType: IdleLogoutReason | 'manual' = 'manual'): void => {
    // Tear down the idle timer immediately
    if (idleTimerRef.current) {
      idleTimerRef.current.destroy();
      idleTimerRef.current = null;
    }

    // End the backend session (fire-and-forget — never block logout)
    const sid = sessionIdRef.current;
    sessionIdRef.current = null;
    if (sid) {
      endSession(sid, { logout_type: logoutType }).catch(() => {});
    }

    setActiveEmail('');
    setActiveRoleName('');
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    localStorage.removeItem('loginUser');
    localStorage.removeItem('user');
    localStorage.removeItem(LAST_AUTH_VIEW_STORAGE_KEY);
    localStorage.removeItem(SESSION_ID_STORAGE_KEY);
    localStorage.removeItem(SESSION_LOGIN_AT_KEY);
    setAllowedSidebarViews(['dashboard']);
    setView('login');
  }, []);

  const requestLogout = (): void => {
    setProfileAnchorEl(null);
    setIsLogoutConfirmOpen(true);
  };

  const clearMasterMenuCloseTimer = (): void => {
    if (masterMenuCloseTimerRef.current) {
      window.clearTimeout(masterMenuCloseTimerRef.current);
      masterMenuCloseTimerRef.current = null;
    }
  };

  const openMasterMenu = (anchorEl: HTMLElement): void => {
    clearMasterMenuCloseTimer();
    setMasterMenuAnchorEl(anchorEl);
  };

  const scheduleMasterMenuClose = (): void => {
    clearMasterMenuCloseTimer();
    masterMenuCloseTimerRef.current = window.setTimeout(() => {
      setMasterMenuAnchorEl(null);
      masterMenuCloseTimerRef.current = null;
    }, 280);
  };

  const navigateToView = useCallback((nextView: AppView, options?: { withLoader?: boolean }): void => {
    const withLoader = options?.withLoader ?? false;

    if (transitionTimerRef.current) {
      window.clearTimeout(transitionTimerRef.current);
      transitionTimerRef.current = null;
    }

    setProfileAnchorEl(null);

    if (!withLoader || nextView === view) {
      setView(nextView);
      return;
    }

    setIsModuleLoading(true);
    transitionTimerRef.current = window.setTimeout(() => {
      setView(nextView);
      setIsModuleLoading(false);
      transitionTimerRef.current = null;
    }, 450);
  }, [view]);

  const handleSidebarNavigation = (nextView: AppView): void => {
    clearMasterMenuCloseTimer();
    setMasterMenuAnchorEl(null);
    navigateToView(nextView, { withLoader: true });
  };

  const goHomeFromNotFound = useCallback((): void => {
    window.history.replaceState({}, '', '/');

    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    if (token) {
      navigateToView('dashboard', { withLoader: true });
      return;
    }

    navigateToView('login', { withLoader: true });
  }, [navigateToView]);

  // Idle-logout is currently disabled at both call sites below; kept intact
  // (rather than deleted) so it can be switched back on without rebuilding
  // the IdleTimer wiring.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const startIdleTracking = (loginAt: number): void => {
    if (idleTimerRef.current) {
      idleTimerRef.current.destroy();
    }
    idleTimerRef.current = new IdleTimer({
      loginTime: loginAt,
      onIdleLogout: (reason: IdleLogoutReason) => {
        handleLogout(reason);
      },
    });
  };

  const initializeSession = async (
    user: { name?: string; email?: string; role_name?: string } | null,
    loginAt: number,
  ): Promise<void> => {
    localStorage.setItem(SESSION_LOGIN_AT_KEY, String(loginAt));
    try {
      const response = await createSession({
        user_name: user?.name ?? 'Unknown',
        user_email: user?.email ?? '',
        user_role: user?.role_name ?? '',
      });
      if (response.success && response.data?.session_id) {
        sessionIdRef.current = response.data.session_id;
        localStorage.setItem(SESSION_ID_STORAGE_KEY, response.data.session_id);
      }
    } catch {
      // Session tracking failure must never block the user
    }
    // startIdleTracking(loginAt);
  };

  const loginUser = useCallback(
    async (email: string, password: string): Promise<void> => {
      const payload = await login({ email, password });

      if (!payload?.success) {
        throw new Error(payload?.message || 'Login failed.');
      }

      const normalizedEmail = payload.data?.user?.email || email;
      const normalizedRoleName = payload.data?.user?.role_name || '';
      const token = payload.data?.token;

      setActiveEmail(normalizedEmail);
      setActiveRoleName(normalizedRoleName);
      setOtpFlow('login');

      if (token) {
        localStorage.setItem('authToken', token);
      }

      if (payload.data?.user) {
        localStorage.setItem('authUser', JSON.stringify(payload.data.user));
        setAllowedSidebarViews(getAllowedViewsFromUserPermissions());
      } else {
        setAllowedSidebarViews(['dashboard']);
      }

      await fetchAndCheckVersion();

      // Start session tracking — fire-and-forget, must not delay navigation
      void initializeSession(payload.data?.user ?? null, Date.now());

      if (payload.data?.user?.first_login) {
        navigateToView('force-change-password', { withLoader: true });
        return;
      }

      navigateToView('dashboard', { withLoader: true });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchAndCheckVersion, navigateToView]
  );

  const submitForcedPasswordChange = useCallback(
    async (payload: { new_password: string; confirm_password: string }): Promise<void> => {
      const response = await forceChangePasswordRequest(payload);

      if (!response?.success) {
        throw new Error(response?.message || 'Unable to change password.');
      }

      // Keep the locally-stored user snapshot in sync so a refresh right
      // after this doesn't re-trigger the forced screen.
      const storedUserRaw = localStorage.getItem('authUser');
      if (storedUserRaw) {
        try {
          const storedUser = JSON.parse(storedUserRaw) as Record<string, unknown>;
          localStorage.setItem('authUser', JSON.stringify({ ...storedUser, first_login: false }));
        } catch {
          // Non-fatal — worst case a refresh re-shows this screen once more.
        }
      }

      navigateToView('dashboard', { withLoader: true });
    },
    [navigateToView]
  );

  const verifyOtp = useCallback(
    async (otp: string): Promise<void> => {
      if (!activeEmail) {
        throw new Error('Email not found. Please login again.');
      }

      const payload = await verifyOtpRequest({ email: activeEmail, otp });

      if (!payload?.success) {
        throw new Error(payload?.message || 'OTP verification failed.');
      }

      if (payload.data?.token) {
        localStorage.setItem('authToken', payload.data.token);
      }

      setAllowedSidebarViews(getAllowedViewsFromUserPermissions());
      await fetchAndCheckVersion();

      navigateToView('dashboard', { withLoader: true });
    },
    [activeEmail, fetchAndCheckVersion, navigateToView]
  );

  const requestPasswordResetOtp = useCallback(async (email: string): Promise<void> => {
    const payload = await forgotPasswordRequest({ email });

    if (!payload?.success) {
      throw new Error(payload?.message || 'Unable to send OTP. Please try again.');
    }

    setActiveEmail(email);
    setOtpFlow('forgot-password');
    showToast('success', payload?.message || 'OTP sent to your email.');
    navigateToView('verify-otp', { withLoader: true });
  }, [navigateToView]);

  const verifyPasswordResetOtp = useCallback(
    async (otp: string): Promise<void> => {
      if (!activeEmail) {
        throw new Error('Email not found. Please restart the password reset flow.');
      }

      const payload = await verifyOtpRequest({ email: activeEmail, otp });

      if (!payload?.success) {
        throw new Error(payload?.message || 'OTP verification failed.');
      }

      navigateToView('reset-password', { withLoader: true });
    },
    [activeEmail, navigateToView]
  );

  const submitPasswordReset = useCallback(
    async (newPassword: string): Promise<void> => {
      if (!activeEmail) {
        throw new Error('Reset session expired. Please restart the password reset flow.');
      }

      const payload = await resetPasswordRequest({
        email: activeEmail,
        password: newPassword,
        confirm_password: newPassword,
      });

      if (!payload?.success) {
        throw new Error(payload?.message || 'Unable to reset password. Please try again.');
      }

      setOtpFlow('login');
      showToast('success', payload?.message || 'Password reset successfully. Please login.');
      navigateToView('login', { withLoader: true });
    },
    [activeEmail, navigateToView]
  );

  useEffect(() => {
    const shouldPersistView = AUTHENTICATED_VIEWS.includes(view);
    if (!shouldPersistView) {
      return;
    }

    localStorage.setItem(LAST_AUTH_VIEW_STORAGE_KEY, view);
  }, [view]);

  useEffect(() => {
    if (didRestoreSessionRef.current) {
      return;
    }

    didRestoreSessionRef.current = true;

    if (initialSessionRef.current.tokenExpired) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('token');
      localStorage.removeItem('authUser');
      localStorage.removeItem('loginUser');
      localStorage.removeItem('user');
      localStorage.removeItem(LAST_AUTH_VIEW_STORAGE_KEY);
      setActiveEmail('');
      setActiveRoleName('');
      setView('login');
      showToast('warning', 'Session timed out. Please login again.');
      return;
    }

    setAllowedSidebarViews(getAllowedViewsFromUserPermissions());
    void fetchAndCheckVersion();

    // Restore idle tracking for an already-authenticated session (e.g. page refresh)
    const storedSessionId = localStorage.getItem(SESSION_ID_STORAGE_KEY);
    const storedLoginAt = Number(localStorage.getItem(SESSION_LOGIN_AT_KEY)) || Date.now();
    if (storedSessionId) {
      sessionIdRef.current = storedSessionId;
      // startIdleTracking(storedLoginAt);
    } else {
      void initializeSession(parseStoredUser(), storedLoginAt);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAndCheckVersion]);

  // Destroy idle timer when the component unmounts (dev StrictMode double-invoke safety)
  useEffect(() => {
    return () => {
      idleTimerRef.current?.destroy();
    };
  }, []);

  useEffect(() => {
    if (!AUTHENTICATED_VIEWS.includes(view)) {
      return;
    }

    if (allowedSidebarViews.includes(view)) {
      return;
    }

    const fallbackView = allowedSidebarViews[0] || 'dashboard';
    setView(fallbackView);
  }, [allowedSidebarViews, view]);

  useEffect(() => {
    return () => {
      if (transitionTimerRef.current) {
        window.clearTimeout(transitionTimerRef.current);
      }
      if (masterMenuCloseTimerRef.current) {
        window.clearTimeout(masterMenuCloseTimerRef.current);
      }
    };
  }, []);

  const content = useMemo(() => {
    if (view === 'forgot-password') {
      return (
        <ForgotPasswordPage
          defaultEmail={activeEmail}
          onBack={() => navigateToView('login', { withLoader: true })}
          onSubmit={requestPasswordResetOtp}
        />
      );
    }

    if (view === 'verify-otp') {
      return (
        <VerifyOtpPage
          email={activeEmail}
          onBack={() => navigateToView(otpFlow === 'forgot-password' ? 'forgot-password' : 'login', { withLoader: true })}
          onVerify={otpFlow === 'forgot-password' ? verifyPasswordResetOtp : verifyOtp}
        />
      );
    }

    if (view === 'reset-password') {
      return (
        <ResetPasswordPage
          onBack={() => navigateToView('verify-otp', { withLoader: true })}
          onReset={submitPasswordReset}
        />
      );
    }

    if (view === 'force-change-password') {
      return (
        <ForceChangePasswordPage
          email={activeEmail}
          onSubmit={submitForcedPasswordChange}
          onLogout={() => handleLogout('manual')}
        />
      );
    }

    if (view === 'dashboard') {
      return (
        <ProtectedModuleRoute moduleName="Dashboard" onGoHome={goHomeFromNotFound}>
          <DashboardPage
            onLogout={() => navigateToView('login', { withLoader: true })}
            currentUserRole={isAdminUser ? 'admin' : 'user'}
            onNavigate={(nextView) => {
              if (allowedSidebarViews.includes(nextView)) {
                navigateToView(nextView, { withLoader: true });
              }
            }}
            onNavigateToPaymentPosting={() => {
              if (allowedSidebarViews.includes('departments-module')) {
                setDepartmentsInitialTab('payment-posting');
                navigateToView('departments-module', { withLoader: true });
              }
            }}
          />
        </ProtectedModuleRoute>
      );
    }

    if (view === 'user-management') {
      return (
        <ProtectedModuleRoute moduleName="User Management" onGoHome={goHomeFromNotFound}>
          <UserManagementPage onBackToDashboard={() => navigateToView('dashboard', { withLoader: true })} />
        </ProtectedModuleRoute>
      );
    }

    if (view === 'role-management') {
      return (
        <ProtectedModuleRoute moduleName="Role Management" onGoHome={goHomeFromNotFound}>
          <RoleManagementPage onBackToDashboard={() => navigateToView('dashboard', { withLoader: true })} />
        </ProtectedModuleRoute>
      );
    }

    if (view === 'settings-workflow') {
      return (
        <ProtectedModuleRoute moduleName="Settings" onGoHome={goHomeFromNotFound}>
          <SettingsPage onBackToDashboard={() => navigateToView('dashboard', { withLoader: true })} />
        </ProtectedModuleRoute>
      );
    }

    if (view === 'departments-module') {
      return (
        <ProtectedModuleRoute moduleName="Departments Module" onGoHome={goHomeFromNotFound}>
          <DepartmentsModulePage
            onBackToDashboard={() => navigateToView('dashboard', { withLoader: true })}
            initialDepartment={departmentsInitialTab}
          />
        </ProtectedModuleRoute>
      );
    }

    if (view === 'assignment-tracker') {
      return (
        <ProtectedModuleRoute moduleName="Assignment Tracker" onGoHome={goHomeFromNotFound}>
          <AssignmentTrackerPage onBackToDashboard={() => navigateToView('dashboard', { withLoader: true })} />
        </ProtectedModuleRoute>
      );
    }

    if (view === 'approvals-flow') {
      return (
        <ProtectedModuleRoute moduleName="Approvals Flow" onGoHome={goHomeFromNotFound}>
          <ApprovalsFlowPage
            onBackToDashboard={() => navigateToView('dashboard', { withLoader: true })}
            currentUserEmail={activeEmail}
            isAdmin={isAdminUser}
          />
        </ProtectedModuleRoute>
      );
    }

    if (view === 'audit-approvals') {
      return (
        <ProtectedModuleRoute moduleName="Audit Approvals" onGoHome={goHomeFromNotFound}>
          <AuditApprovalsPage
            onBackToDashboard={() => navigateToView('dashboard', { withLoader: true })}
            currentUserEmail={activeEmail}
            isAdmin={isAdminUser}
            currentUserRoleName={activeRoleName}
          />
        </ProtectedModuleRoute>
      );
    }

    if (view === 'dispute-actions') {
      return (
        <ProtectedModuleRoute moduleName="Dispute Actions" onGoHome={goHomeFromNotFound}>
          <DisputeActionsPage
            onBackToDashboard={() => navigateToView('dashboard', { withLoader: true })}
            isAdmin={isAdminUser}
          />
        </ProtectedModuleRoute>
      );
    }

    if (view === 'email-templates') {
      return (
        <ProtectedModuleRoute moduleName="Email Templates" onGoHome={goHomeFromNotFound}>
          <EmailTemplatesPage onBackToDashboard={() => navigateToView('dashboard', { withLoader: true })} />
        </ProtectedModuleRoute>
      );
    }

    if (view === 'support-module') {
      return (
        <ProtectedModuleRoute moduleName="Support Module" onGoHome={goHomeFromNotFound}>
          <SupportModulePage onBackToDashboard={() => navigateToView('dashboard', { withLoader: true })} />
        </ProtectedModuleRoute>
      );
    }

    if (view === 'ticket-bucket') {
      return (
        <ProtectedModuleRoute moduleName="Ticket Bucket" onGoHome={goHomeFromNotFound}>
          <TicketBucketPage
            onBackToDashboard={() => navigateToView('dashboard', { withLoader: true })}
            currentUserEmail={activeEmail}
            isAdmin={isAdminUser}
            currentUserRoleName={activeRoleName}
          />
        </ProtectedModuleRoute>
      );
    }

    if (view === 'task-manager') {
      return (
        <ProtectedModuleRoute moduleName="Task Manager" onGoHome={goHomeFromNotFound}>
          <TaskManagerPage onBackToDashboard={() => navigateToView('dashboard', { withLoader: true })} />
        </ProtectedModuleRoute>
      );
    }

    if (view === 'ar-audit') {
      return (
        <ProtectedModuleRoute moduleName="AR Audit" onGoHome={goHomeFromNotFound}>
          <ARAuditPage
            onBackToDashboard={() => navigateToView('dashboard', { withLoader: true })}
            currentUserRoleName={activeRoleName}
            isAdmin={isAdminUser}
          />
        </ProtectedModuleRoute>
      );
    }

    if (view === 'reports-module') {
      return (
        <ProtectedModuleRoute moduleName="Reports Module" onGoHome={goHomeFromNotFound}>
          <ReportsModulePage onBackToDashboard={() => navigateToView('dashboard', { withLoader: true })} />
        </ProtectedModuleRoute>
      );
    }

    if (view === 'audit-logs') {
      return (
        <ProtectedModuleRoute moduleName="Audit Logs" onGoHome={goHomeFromNotFound}>
          <AuditLogsPage onBackToDashboard={() => navigateToView('dashboard', { withLoader: true })} />
        </ProtectedModuleRoute>
      );
    }

    if (view === 'clients-module') {
      return (
        <ProtectedModuleRoute moduleName="Clients Module" onGoHome={goHomeFromNotFound}>
          <ClientsModulePage onBackToDashboard={() => navigateToView('dashboard', { withLoader: true })} />
        </ProtectedModuleRoute>
      );
    }

    if (view === 'ar-categories-module') {
      return (
        <ProtectedModuleRoute moduleName="AR Category & Sub-Category" onGoHome={goHomeFromNotFound}>
          <ArCategoriesModulePage onBackToDashboard={() => navigateToView('dashboard', { withLoader: true })} />
        </ProtectedModuleRoute>
      );
    }

    if (view === 'error-parameters-module') {
      return (
        <ProtectedModuleRoute moduleName="Error Parameters" onGoHome={goHomeFromNotFound}>
          <ErrorParametersModulePage onBackToDashboard={() => navigateToView('dashboard', { withLoader: true })} />
        </ProtectedModuleRoute>
      );
    }

    if (view === 'payer-module') {
      return (
        <ProtectedModuleRoute moduleName="Payer Module" onGoHome={goHomeFromNotFound}>
          <PayerModulePage onBackToDashboard={() => navigateToView('dashboard', { withLoader: true })} />
        </ProtectedModuleRoute>
      );
    }

    if (view === 'not-found') {
      return <NotFoundPage onGoHome={goHomeFromNotFound} />;
    }

    return (
      <LoginPage
        onLogin={loginUser}
        onForgotPassword={() => navigateToView('forgot-password', { withLoader: true })}
      />
    );
  }, [
    activeEmail,
    activeRoleName,
    allowedSidebarViews,
    departmentsInitialTab,
    goHomeFromNotFound,
    handleLogout,
    isAdminUser,
    loginUser,
    navigateToView,
    otpFlow,
    verifyOtp,
    requestPasswordResetOtp,
    verifyPasswordResetOtp,
    submitPasswordReset,
    submitForcedPasswordChange,
    view,
  ]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Toaster
        position="top-center"
        offset="80px"
        closeButton
        visibleToasts={3}
        toastOptions={{
          duration: 3200,
          style: {
            borderRadius: '12px',
          },
        }}
      />
      <Box className="App" sx={{ display: 'flex', minHeight: '100vh' }}>
        <AppBar position="fixed" elevation={0} sx={{ zIndex: (muiTheme) => muiTheme.zIndex.drawer + 1 }}>
          <Toolbar>
            {showSidebar ? (
              <IconButton
                onClick={() => setSidebarExpanded((prev) => !prev)}
                aria-label="toggle sidebar"
                sx={{ color: '#ffffff', mr: 1.25 }}
              >
                {sidebarExpanded ? <MenuOpenRoundedIcon /> : <MenuRoundedIcon />}
              </IconButton>
            ) : null}
            <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700,color: '#ffffff' }}>
              Swagpro Medical Billing Dashboard
            </Typography>
            <Chip
              label={APP_VIEW_LABEL[view]}
              sx={{ backgroundColor: '#ffffff', color: BRAND.heading, fontWeight: 700 }}
            />
            {showSidebar ? (
              <IconButton
                onClick={(event) => setProfileAnchorEl(event.currentTarget)}
                aria-label="open profile"
                sx={{ color: '#ffffff', ml: 1 }}
              >
                <ProfileIcon size={24} />
              </IconButton>
            ) : null}
          </Toolbar>
        </AppBar>

        <Popover
          open={Boolean(profileAnchorEl)}
          anchorEl={profileAnchorEl}
          onClose={() => setProfileAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              mt: 1,
              width: 280,
              borderRadius: 2,
              border: `1px solid ${BRAND.border}`,
              boxShadow: '0 8px 24px rgba(208,20,67,0.14)',
            },
          }}
        >
          <Stack spacing={1.1} sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <WbTwilightRoundedIcon sx={{ color: BRAND.heading, fontSize: 20 }} />
              <Typography fontWeight={700} sx={{ color: BRAND.heading }}>
                {greeting}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <AccessTimeRoundedIcon sx={{ color: '#9b6f7e', fontSize: 18 }} />
              <Typography variant="caption" color="text.secondary">
                {formattedTime}
              </Typography>
            </Stack>

            <Divider />

            <Stack direction="row" spacing={1} alignItems="center">
              <BadgeRoundedIcon sx={{ color: BRAND.heading, fontSize: 19 }} />
              <Typography fontWeight={700}>{profileName}</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <EmailRoundedIcon sx={{ color: '#9b6f7e', fontSize: 18 }} />
              <Typography variant="body2" color="text.secondary">
                {activeEmail || 'user@company.com'}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <AdminPanelSettingsRoundedIcon sx={{ color: '#9b6f7e', fontSize: 18 }} />
              <Typography variant="body2" color="text.secondary">
                Role: {activeRoleName || 'User'}
              </Typography>
            </Stack>

            <Divider />

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ cursor: 'pointer', color: BRAND.heading }}
              onClick={() => {
                setProfileAnchorEl(null);
                setIsChangePasswordOpen(true);
              }}
            >
              <LockResetRoundedIcon sx={{ fontSize: 18 }} />
              <Typography fontWeight={700}>Change Password</Typography>
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ cursor: 'pointer', color: BRAND.heading }}
              onClick={requestLogout}
            >
              <LogoutIcon size={18} />
              <Typography fontWeight={700}>Logout</Typography>
            </Stack>
          </Stack>
        </Popover>

        <CommonConfirmDialog
          open={isLogoutConfirmOpen}
          title="Confirm Logout"
          message="Do you really want to logout and go to login page?"
          confirmLabel="Logout"
          cancelLabel="Cancel"
          onCancel={() => setIsLogoutConfirmOpen(false)}
          onConfirm={() => {
            setIsLogoutConfirmOpen(false);
            handleLogout('manual');
          }}
        />

        <ChangePasswordModal
          open={isChangePasswordOpen}
          onClose={() => setIsChangePasswordOpen(false)}
          currentUserEmail={activeEmail}
        />

        <CommonModal
          open={versionDialogOpen}
          title="New Version Available"
          onClose={() => setVersionDialogOpen(false)}
          maxWidth="sm"
          actions={(
            <>
              <CommonButton label="Later" variant="outlined" onClick={() => setVersionDialogOpen(false)} />
              <CommonButton
                label="Hard Refresh"
                startIcon={<SystemUpdateAltRoundedIcon />}
                onClick={handleHardRefresh}
              />
            </>
          )}
        >
          <Stack spacing={1.25}>
            <Typography variant="body2" color="text.secondary">
              A new version of the application is available. Please do a hard refresh (Ctrl + Shift + R) to get the latest changes.
            </Typography>
            <Typography variant="body2">
              <strong>Current:</strong> {localStorage.getItem(APP_VERSION_STORAGE_KEY) || APP_METADATA.version}
            </Typography>
            <Typography variant="body2">
              <strong>Latest:</strong> {latestVersion || currentVersion}
            </Typography>
            {latestUpdateTitle ? (
              <Typography variant="body2">
                <strong>Update:</strong> {latestUpdateTitle}
              </Typography>
            ) : null}
            {latestUpdateDescription ? (
              <Typography variant="body2" color="text.secondary">
                {latestUpdateDescription}
              </Typography>
            ) : null}
          </Stack>
        </CommonModal>

        {showSidebar ? <ChatBot /> : null}

        {showSidebar ? (
          <Drawer
            variant="permanent"
            sx={{
              width: sidebarExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
              flexShrink: 0,
              transition: 'width 260ms cubic-bezier(0.4,0,0.2,1)',
              '& .MuiDrawer-paper': {
                width: sidebarExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
                boxSizing: 'border-box',
                top: 64,
                height: 'calc(100vh - 64px)',
                borderRight: `1px solid ${BRAND.border}`,
                backgroundColor: '#ffffff',
                overflowX: 'hidden',
                overflowY: 'auto',
                scrollbarWidth: 'thin',
                scrollbarColor: `${BRAND.heading} #e6f1f4`,
                '&::-webkit-scrollbar': {
                  width: 6,
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#e6f1f4',
                  borderRadius: 8,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: BRAND.heading,
                  borderRadius: 8,
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: '#3f8593',
                },
                transition: 'width 260ms cubic-bezier(0.4,0,0.2,1)',
                willChange: 'width',
              },
            }}
          >
            <List sx={{ px: 1 }}>
              {visibleNavItems.map((item) => (
                <React.Fragment key={item.key}>
                  <ListItem disablePadding sx={{ mb: 0.5 }}>
                    <Tooltip title={sidebarExpanded ? '' : item.label} placement="right" arrow>
                      <ListItemButton
                        selected={view === item.key}
                        onClick={() => handleSidebarNavigation(item.key)}
                        sx={{
                          minHeight: 46,
                          justifyContent: sidebarExpanded ? 'initial' : 'center',
                          borderRadius: 2,
                          px: 1.5,
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: sidebarExpanded ? 1.5 : 0,
                            justifyContent: 'center',
                            color: view === item.key ? BRAND.heading : 'rgba(76, 155, 171, 0.72)',
                            transition: 'margin-right 260ms cubic-bezier(0.4,0,0.2,1)',
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          sx={{
                            opacity: sidebarExpanded ? 1 : 0,
                            maxWidth: sidebarExpanded ? 200 : 0,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            transition: 'opacity 200ms cubic-bezier(0.4,0,0.2,1), max-width 260ms cubic-bezier(0.4,0,0.2,1)',
                            m: 0,
                          }}
                        />
                      </ListItemButton>
                    </Tooltip>
                  </ListItem>
                  {(item.key === 'dashboard' || (!hasDashboardNavItem && item.key === visibleNavItems[0]?.key)) && visibleMasterModuleItems.length > 0 ? (
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                      <Tooltip title={sidebarExpanded ? '' : 'Master Module'} placement="right" arrow>
                        <ListItemButton
                          selected={isMasterModuleActive}
                          onMouseEnter={(event) => openMasterMenu(event.currentTarget)}
                          onMouseLeave={scheduleMasterMenuClose}
                          onClick={() => handleSidebarNavigation(visibleMasterModuleItems[0].key)}
                          sx={{
                            minHeight: 46,
                            justifyContent: sidebarExpanded ? 'initial' : 'center',
                            borderRadius: 2,
                            px: 1.5,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: sidebarExpanded ? 1.5 : 0,
                              justifyContent: 'center',
                              color: isMasterModuleActive ? BRAND.heading : 'rgba(76, 155, 171, 0.72)',
                              transition: 'margin-right 260ms cubic-bezier(0.4,0,0.2,1)',
                            }}
                          >
                            <SettingsApplicationsRoundedIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary="Master Module"
                            sx={{
                              opacity: sidebarExpanded ? 1 : 0,
                              maxWidth: sidebarExpanded ? 170 : 0,
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              transition: 'opacity 200ms cubic-bezier(0.4,0,0.2,1), max-width 260ms cubic-bezier(0.4,0,0.2,1)',
                              m: 0,
                            }}
                          />
                          {sidebarExpanded ? (
                            <KeyboardArrowRightRoundedIcon sx={{ color: BRAND.heading, fontSize: 20 }} />
                          ) : null}
                        </ListItemButton>
                      </Tooltip>
                    </ListItem>
                  ) : null}
                </React.Fragment>
              ))}
            </List>
            <Popper
              open={isMasterMenuOpen}
              anchorEl={masterMenuAnchorEl}
              placement="right-start"
              modifiers={[
                {
                  name: 'offset',
                  options: {
                    offset: [0, 2],
                  },
                },
                {
                  name: 'preventOverflow',
                  options: {
                    padding: 8,
                  },
                },
              ]}
              sx={{ zIndex: (muiTheme) => muiTheme.zIndex.drawer + 2 }}
            >
              <Paper
                onMouseEnter={clearMasterMenuCloseTimer}
                onMouseLeave={scheduleMasterMenuClose}
                sx={{
                  ml: 0.75,
                  minWidth: 270,
                  borderRadius: 2,
                  border: `1px solid ${BRAND.border}`,
                  boxShadow: '0 12px 28px rgba(76,155,171,0.18)',
                  overflow: 'hidden',
                }}
              >
                <List sx={{ py: 0.75, backgroundColor: '#ffffff' }}>
                  {visibleMasterModuleItems.map((item) => (
                    <ListItem key={item.key} disablePadding>
                      <ListItemButton
                        selected={view === item.key}
                        onClick={() => handleSidebarNavigation(item.key)}
                        sx={{
                          minHeight: 42,
                          px: 1.5,
                          gap: 1.25,
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 28,
                            color: view === item.key ? BRAND.heading : 'rgba(76, 155, 171, 0.72)',
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{ fontSize: 14, fontWeight: view === item.key ? 700 : 600 }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Popper>
          </Drawer>
        ) : null}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            overflowX: 'hidden',
            mt: '64px',
            minHeight: 'calc(100vh - 64px)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0 }}>
            {isModuleLoading ? (
              <CommonLoader fullScreen text="Loading module..." size={96} />
            ) : (
              <ErrorBoundary key={view} onGoHome={goHomeFromNotFound}>
                <Suspense fallback={<CommonLoader fullScreen text="Loading module..." size={96} />}>
                  {content}
                </Suspense>
              </ErrorBoundary>
            )}
          </Box>
          <Box
            component="footer"
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.25,
              borderTop: `1px solid ${BRAND.border}`,
              backgroundColor: 'rgba(255,255,255,0.92)',
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={0.5}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
            >
              <Typography variant="caption" sx={{ color: '#6d3d4a', fontWeight: 700 }}>
                {APP_METADATA.companyName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Version {currentVersion} | Last updated {formattedLastUpdated}
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
